import React from 'react';
import { Task, TaskCategory, TaskPriority } from '../types/todo';
import { X } from 'lucide-react';

interface TaskFormProps {
  onSubmit: (task: Partial<Task>) => void;
  onClose: () => void;
  initialTask?: Partial<Task>;
}

export function TaskForm({ onSubmit, onClose, initialTask }: TaskFormProps) {
  const [title, setTitle] = React.useState(initialTask?.title || '');
  const [description, setDescription] = React.useState(initialTask?.description || '');
  const [priority, setPriority] = React.useState<TaskPriority>(initialTask?.priority || 'medium');
  const [category, setCategory] = React.useState<TaskCategory>(initialTask?.category || 'before-departure');

  // Constants for character limits
  const TITLE_MAX_LENGTH = 100;
  const DESCRIPTION_MAX_LENGTH = 1000;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      priority,
      category,
      status: 'not-started',
      subtasks: [],
      notes: ''
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />
        <div className="flex items-center justify-between p-4 sm:p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {initialTask ? 'Modifier la tache' : 'Nouvelle tache'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 sm:p-1 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 pb-6 sm:p-4 space-y-5 sm:space-y-6">
          <div>
            <div className="flex items-center justify-between mb-1.5 sm:mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Titre *
              </label>
              <span className={`text-xs ${
                title.length >= TITLE_MAX_LENGTH * 0.9
                  ? 'text-red-500 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {title.length}/{TITLE_MAX_LENGTH}
              </span>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={TITLE_MAX_LENGTH}
              className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5 sm:mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <span className={`text-xs ${
                description.length >= DESCRIPTION_MAX_LENGTH * 0.9
                  ? 'text-red-500 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX_LENGTH}
              rows={5}
              className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white resize-none text-base sm:text-sm"
              placeholder="Decrivez la tache..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-1">
                Categorie *
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TaskCategory)}
                className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                required
              >
                <option value="before-departure">Preparer votre depart</option>
                <option value="after-arrival">Le jour du depart</option>
                <option value="return">Les demarches a l'arrivee</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-1">
                Priorite
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-xl sm:rounded-lg transition-colors touch-manipulation"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 rounded-xl sm:rounded-lg transition-colors touch-manipulation"
            >
              {initialTask ? 'Enregistrer' : 'Creer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}